import { formatNumber } from './numbers';
import coverageProductMap from 'src/views/claims/create-claim/coverage-product-map';

/**
 * @param {{currentDisposition: string, approvedAmount: number|undefined}} claim
 * @returns {string}
 */
export const getAmountApproved = claim => {
  let amount = 0;
  amount = isClaimPaid(claim) ? claim.paidAmount : claim.approvedAmount;
  const formattedNumber = formatNumber(
    amount ?? 0,
    { useGrouping: true }
  );

  return `$${formattedNumber}`;
};

export const mapStatusCodeToLabel = statusCode => {
  switch (statusCode) {
    case 'O':
    case 'Open':
      return 'Open';
    default:
    case 'C':
      return 'Closed';
  }
};

export const getClaimProduct = (claim) => {
  const coverageId = claim.coverageId

  if (coverageId) {
    const product = coverageProductMap[coverageId];
    if (product) {
      return product.productName.toUpperCase();
    }
  }
  return claim.claimType;
}

export const mapTimeRangeToInteger = (timeRange) => {
  switch (timeRange) {
    case 'Today':
      return 0;
    case 'Yesterday':
      return 1;
    case 'Last 7 Days':
      return 7;
    case 'Last 30 Days':
      return 30;
    case 'Last 60 Days':
      return 60;
    case 'Last 90 Days':
      return 90;
    case 'All Time':
      return Infinity;
  }
}

/**
 * @param {{firstName: string, lastName: string}} user 
 * @returns {string}
 */
export const generateUserName = (user) => {
  const MAX_USER_NAME_LENGTH = 15;
  const upperBound = Math.min(user.lastName.length, MAX_USER_NAME_LENGTH - 1);

  return user.firstName[0] + user.lastName.slice(0, upperBound);
}

/**
 * @param {{attachments: {files: object[]}[]}} claimDetail 
 * @returns {number}
 */
export const getFileCountFromClaimDetail = (documentData) => {
  const categories = Object.keys(documentData);
  let subCategories = [];
  if (categories.length > 0) {
    categories.forEach(category => {
      subCategories = [...subCategories, ...documentData[category]];
    });
  }
  return subCategories.reduce((fileCount, subCategory) => {
    const attachmentCount = subCategory.attachments?.length ?? 0;
    return fileCount + attachmentCount;
  }, 0);
}

const hasFeature = (features, featureType, featureValue) => {
  if (!Array.isArray(features)) {
    return false;
  }

  return features.some(featureGroup => {
    if (Array.isArray(featureGroup)) {
      return featureGroup.some(
        feature => feature?.label === featureType && feature?.value === featureValue
      );
    }

    return featureGroup?.label === featureType && featureGroup?.value === featureValue;
  });
}
const isPaidOrClosedWithoutPayment = dispositionValue => {
  const normalizedDisposition = `${dispositionValue || ''}`.toLowerCase().trim();

  return (
    normalizedDisposition === 'paid' || normalizedDisposition === 'closed without payment' || normalizedDisposition === 'denied'
  );
};

const hasAllFeaturesPaidOrClosedWithoutPayment = features => {
  if (!Array.isArray(features) || features.length === 0) {
    return false;
  }

  return features.every(featureGroup => {
    if (Array.isArray(featureGroup)) {
      const disposition = featureGroup.find(
        feature => `${feature?.label || ''}`.toLowerCase() === 'disposition'
      );
      return isPaidOrClosedWithoutPayment(disposition?.value);
    }

    if (`${featureGroup?.label || ''}`.toLowerCase() === 'disposition') {
      return isPaidOrClosedWithoutPayment(featureGroup?.value);
    }

    return false;
  });
};

const isClosedStatus = status => {
  const normalizedStatus = `${status || ''}`.toLowerCase();
  return normalizedStatus === 'closed' || normalizedStatus === 'c';
}

export const getAreCoveragesPaid = (claimDetail) => {
  const features = claimDetail?.features ?? [];
  const isFeatureCoverage = claimDetail?.featureKey === 'coverage';
  const areAllFeaturesPaid = hasAllFeaturesPaidOrClosedWithoutPayment(features);
  const hasClaimPaid = hasFeature(features, 'Disposition', 'Paid');
  return isFeatureCoverage && (areAllFeaturesPaid && hasClaimPaid);
};

export const isClaimPaid = (claim) => {
  const isDispositionPaid = claim?.currentDisposition === 'Paid';
  const areCoveragesPaid = getAreCoveragesPaid(claim?.claimDetail);

  return areCoveragesPaid || isDispositionPaid;
};